import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.__supabase = supabase;
}

function toISODate(val) {
  if (!val) return null;
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;

  return d.toISOString().slice(0, 10);
}

export const TASK_SELECT = `
  *,
  teams:team_id(id, name),
  sub_teams:sub_team_id(id, name),
  task_assignees(
    id,
    task_id,
    user_id,
    assigned_by,
    assigned_at,
    profiles:profiles!task_assignees_user_id_fkey(
      id,
      name,
      email,
      role,
      photo_url,
      team_id,
      department_id,
      sub_team_ids
    )
  ),
  task_user_cases(*),
  task_updates(*),
  task_notes(*),
  task_issues(*),
  task_extension_requests(*)
`;

function cleanTaskPayload(taskOrUpdates) {
  const payload = { ...taskOrUpdates };

  if (payload.startDate) {
    payload.start_date = toISODate(payload.startDate);
    delete payload.startDate;
  }

  if (payload.dueDate) {
    payload.due_date = toISODate(payload.dueDate);
    delete payload.dueDate;
  }

  if (payload.start_date) payload.start_date = toISODate(payload.start_date);
  if (payload.due_date) payload.due_date = toISODate(payload.due_date);

  if (payload.loopDurationValue) {
    payload.loop_duration_value = payload.loopDurationValue;
    delete payload.loopDurationValue;
  }
  if (payload.loopDurationUnit) {
    payload.loop_duration_unit = payload.loopDurationUnit;
    delete payload.loopDurationUnit;
  }
  if (payload.createNextAfterCompletion) {
    payload.create_next_after_completion = payload.createNextAfterCompletion;
    delete payload.createNextAfterCompletion;
  }

  if (!payload.extended_due_dates) payload.extended_due_dates = [];
  if (!payload.attachments) payload.attachments = [];

  const ALLOWED_TASK_COLUMNS = new Set([
    'id',
    'title',
    'description',
    'assignee_id',
    'start_date',
    'due_date',
    'priority',
    'status',
    'budget',
    'extended_due_dates',
    'team_id',
    'sub_team_id',
    'created_by',
    'updated_at',
    'deleted_at',
    'attachment_url',
    'attachments',
    'assigned_by',
    'progress_status',
    'progress_report',
    'timer_start',
    'hours',
    'extended_due_date_1',
    'extended_due_date_2',
    'assignee',
    'assignee_email',
    'created_at',
    'task_note',
    'task_type',
    'repeat_frequency',
    'repeat_every',
    'repeat_start_date',
    'repeat_end_date',
    'repeat_occurrences',
    'repeat_days',
    'loop_duration_value',
    'loop_duration_unit',
    'create_next_after_completion',
    'loop_series_id',
    'occurrence_index',
    'occurrence_date',
    'is_loop_occurrence'
  ]);

  for (const key of Object.keys(payload)) {
    if (!ALLOWED_TASK_COLUMNS.has(key)) {
      delete payload[key];
    }
  }

  return payload;
}

export async function createTaskSupabase(task) {
  const payload = cleanTaskPayload(task);

  console.log("TASK INSERT PAYLOAD:", payload);

  const { data, error } = await supabase
    .from("tasks")
    .insert([payload])
    .select(TASK_SELECT)
    .single();

  if (error) {
    console.error("Supabase INSERT error:", error);
    throw error;
  }

  return data;
}

export async function updateTaskSupabase(taskId, updates) {
  const payload = cleanTaskPayload(updates);

  const { data, error } = await supabase
    .from("tasks")
    .update(payload)
    .eq("id", taskId)
    .select(TASK_SELECT)
    .single();

  if (error) {
    console.error("Supabase UPDATE error:", error);
    throw error;
  }

  return data;
}

export async function fetchTasksSupabase() {
  const { data, error } = await supabase
    .from("tasks")
    .select(TASK_SELECT)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Supabase FETCH TASKS error:", error);
    throw error;
  }

  return data;
}

export async function deleteTaskSupabase(taskId) {
  const { data, error } = await supabase
    .from("tasks")
    .delete()
    .eq("id", taskId);

  if (error) {
    console.error("Supabase DELETE TASK error:", error);
    throw error;
  }

  return data;
}

export async function addTaskAssigneeSupabase({
  taskId,
  userId,
  assignedBy,
}) {
  const { data, error } = await supabase
    .from("task_assignees")
    .insert([
      {
        task_id: taskId,
        user_id: userId,
        assigned_by: assignedBy || null,
      },
    ])
    .select(`
      id,
      task_id,
      user_id,
      assigned_by,
      assigned_at,
      profiles(
        id,
        name,
        email,
        role,
        photo_url,
        team_id,
        department_id,
        sub_team_ids
      )
    `)
    .single();

  if (error) {
    console.error("Supabase ADD ASSIGNEE error:", error);
    throw error;
  }

  return data;
}

export async function deleteTaskAssigneesSupabase({
  taskId,
  userIds,
}) {
  const { data, error } = await supabase
    .from("task_assignees")
    .delete()
    .eq("task_id", taskId)
    .in("user_id", userIds);

  if (error) {
    console.error("Supabase DELETE ASSIGNEES error:", error);
    throw error;
  }

  return data;
}